/**
 * MobileSupervisionsMatrixCards — staff-picker drill ( <lg ). Batch 6.4.
 * Consumes exact same /supervisions/matrix payload as desktop. For care
 * workers the hardened backend returns exactly one row (their own).
 */
import React, { useState, useEffect } from "react";
import { CheckCircle2, User, CalendarClock } from "lucide-react";

const DUE_TONES = {
  overdue:  "bg-red-100 text-red-800 border-red-300",
  due_soon: "bg-amber-100 text-amber-900 border-amber-400",
  on_track: "bg-emerald-100 text-emerald-800 border-emerald-300",
  unknown:  "bg-slate-100 text-slate-600 border-slate-300",
};

const QUARTERS = [
  { key: "q1", label: "Q1" }, { key: "q2", label: "Q2" },
  { key: "q3", label: "Q3" }, { key: "q4", label: "Q4" },
  { key: "annual", label: "Annual" },
];

export default function MobileSupervisionsMatrixCards({ matrixData }) {
  const rows = matrixData?.rows || [];
  const [selectedId, setSelectedId] = useState("");
  useEffect(() => {
    if (rows.length === 1) setSelectedId(rows[0].staff_user_id);
    else if (selectedId && !rows.some((r) => r.staff_user_id === selectedId)) setSelectedId("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center" data-testid="mobile-sup-matrix-empty">
        <div className="text-sm font-semibold text-slate-700">No matrix rows.</div>
      </div>
    );
  }

  const selected = rows.find((r) => r.staff_user_id === selectedId);

  return (
    <div className="space-y-3" data-testid="mobile-sup-matrix">
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Select staff member</span>
        <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}
          data-testid="mobile-sup-matrix-picker"
          className="mt-0.5 w-full min-h-[44px] h-11 text-sm border border-slate-300 rounded-md bg-white px-2">
          <option value="">— Pick a staff member —</option>
          {rows.map((r) => (
            <option key={r.staff_user_id} value={r.staff_user_id}>
              {r.staff_name} · {String(r.role || "").replace(/_/g, " ")}
            </option>
          ))}
        </select>
      </label>

      {!selected ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center" data-testid="mobile-sup-matrix-pick">
          <User className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-600">Pick a staff member above.</div>
          <div className="text-[11px] text-slate-500 mt-1">{rows.length} row{rows.length === 1 ? "" : "s"} in scope</div>
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3" data-testid={`mobile-sup-matrix-header-${selected.staff_user_id}`}>
            <div className="w-10 h-10 rounded-full bg-cyan-50 border border-cyan-100 flex items-center justify-center">
              <User className="w-4 h-4 text-cyan-700" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-heading text-base font-semibold text-slate-900 truncate">{selected.staff_name}</div>
              <div className="text-[11px] text-slate-500 capitalize">{String(selected.role || "").replace(/_/g, " ")}</div>
            </div>
          </div>
          <ul className="space-y-2" data-testid="mobile-sup-matrix-quarters">
            {QUARTERS.map((q) => {
              const cell = selected[q.key];
              return (
                <li key={q.key} data-testid={`mobile-sup-matrix-${q.key}-${selected.staff_user_id}`}
                  className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3">
                  <div className="w-12 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{q.label}</div>
                  {cell ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span className="text-xs text-slate-700 tabular-nums">Held {cell.held_date || "—"}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400 italic">Not held</span>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-2 flex-wrap" data-testid={`mobile-sup-matrix-next-${selected.staff_user_id}`}>
            <CalendarClock className="w-4 h-4 text-slate-500" />
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Next Due</div>
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${DUE_TONES[selected.due_state] || DUE_TONES.unknown}`}>
              {selected.next_due_date || "no history"} · {(selected.due_state || "unknown").replace(/_/g, " ")}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
